import {
    Component,
    input,
    signal,
    effect,
    computed,
    viewChild,
    forwardRef,
    Type,
    Signal,
    InputSignal,
    WritableSignal,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { EditorView } from "@codemirror/view";
import { CodeEditor } from "@acrodata/code-editor";
import { LanguageDescription } from "@codemirror/language";
import { Extension, TransactionSpec } from "@codemirror/state";

import { SqlLanguageSpec } from "../models";
import { OnChangeFn, OnTouchedFn } from "../../models";
import { SqlEditorThemeService } from "../services/sql-editor-theme.service";
import { SqlEditorLanguageService } from "../services/sql-editor-language.service";


@Component({
    selector: "sql-editor",
    templateUrl: "./sql-editor.component.html",
    styleUrls: ["./sql-editor.component.scss"],
    providers: [{
        useExisting: forwardRef((): Type<SqlEditorComponent> => SqlEditorComponent),
        provide: NG_VALUE_ACCESSOR,
        multi: true
    }]
})
export class SqlEditorComponent implements ControlValueAccessor {
    // View child statements.
    public editor: Signal<CodeEditor> = viewChild(CodeEditor);

    // Input properties.
    public specification: InputSignal<SqlLanguageSpec> = input();
    public extensions: InputSignal<Extension[]> = input([]);
    public placeholder: InputSignal<string> = input();

    // Computed signals.
    protected language: Signal<LanguageDescription> = this.computeLanguage();

    // Writable signals.
    private onTouched: WritableSignal<OnTouchedFn> = signal(null);
    private onChange: WritableSignal<OnChangeFn> = signal(null);

    protected disabled: WritableSignal<boolean> = signal(false);
    protected theme: WritableSignal<Extension> = signal(null);
    protected value: WritableSignal<string> = signal("");


    constructor(private languageService: SqlEditorLanguageService,
                private themeService: SqlEditorThemeService) {
        this.configureEditor();
    }

    private computeLanguage(): Signal<LanguageDescription> {
        return computed((): LanguageDescription => {
            // Build the language description corresponding to the specification every time it changes.
            return this.languageService.getLanguageDescription(this.specification());
        });
    }

    private configureEditor(): void {
        // Set the editor theme (one-dark theme with minor overrides).
        this.theme.set(this.themeService.getThemeExtension());

        // React to editor apparition or on-touched value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onTouched: OnTouchedFn = this.onTouched();
            editor.registerOnTouched((): void => onTouched?.());
        });

        // React to editor apparition or on-change value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onChange: OnChangeFn = this.onChange();
            editor.registerOnChange((value: string): void => onChange?.(value));
        });
    }

    public insert(text: string): void {
        if (!text?.length) return;

        const editor: CodeEditor = this.editor();
        if (!editor) return;

        const view: EditorView = editor.view;
        if (!view) return;

        const from: number = view?.state?.selection?.main?.from ?? 0;
        const position: number = from + text.length;

        view.dispatch({
            changes: { from, insert: text }, // Insert the text
            selection: { anchor: position }, // Place cursor after inserted text
            scrollIntoView: true             // Scrolls until inserted text becomes visible
        });

        view.focus(); // Restore focus in the editor, for the user to be able to continue writing.
    }

    public dispatch(...specs: TransactionSpec[]): void {
        if (!specs?.length) return;

        const editor: CodeEditor = this.editor();
        if (!editor) return;

        const view: EditorView = editor.view;
        if (!view) return;

        view.dispatch(...specs);
    }

    public registerOnChange(fn: OnChangeFn): void {
        this.onChange.set(fn);
    }

    public registerOnTouched(fn: OnTouchedFn): void {
        this.onTouched.set(fn);
    }

    public writeValue(value: string): void {
        this.value.set(value);
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }
}